/*
  AddNodeForm Styles

  Aligned with component library's DropdownMenu design system.
  Clean, professional styling matching the @usealloy/component-library patterns.
*/

import styled, { keyframes } from 'styled-components';

const slideDownAndFade = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-2px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const AddNodeFormContainer = styled.div<{ showForm?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${props => (props.showForm ? 'var(--cl-juicyBlue-500)' : '#e8eef5')};
  border: 1px solid ${props => (props.showForm ? 'var(--cl-juicyBlue-600)' : '#d0d9e4')};
  border-radius: 50%;
  cursor: pointer;
  transition: all var(--cl-duration-150) var(--cl-motion-ease-out);
  z-index: calc(var(--cl-zindex-popover) + 2); /* Keep button above menu */
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  &:hover {
    background: ${props => (props.showForm ? 'var(--cl-juicyBlue-600)' : '#dde5f0')};
    border-color: ${props => (props.showForm ? 'var(--cl-juicyBlue-700)' : '#c2cdd9')};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }
  
  &:focus:not(:focus-visible) {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }
`;

export const StartHereLabel = styled.div<{ active?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 var(--cl-space-5);
  background: ${props => (props.active ? 'var(--cl-blue-500)' : '#e8eef5')};
  color: ${props => (props.active ? '#ffffff' : 'var(--cl-text-primary)')};
  border: 1px solid ${props => (props.active ? 'var(--cl-blue-600)' : '#d0d9e4')};
  border-radius: var(--cl-radius-2);
  font-weight: var(--cl-font-weight-medium);
  font-size: var(--cl-font-size-2);
  font-family: var(--cl-font-family-default);
  cursor: pointer;
  transition: all var(--cl-duration-150) var(--cl-motion-ease-out);
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  z-index: calc(var(--cl-zindex-popover) + 2); /* Keep button above menu */

  &:hover {
    background: ${props => (props.active ? 'var(--cl-blue-600)' : '#dde5f0')};
    color: ${props => (props.active ? '#ffffff' : 'var(--cl-text-primary)')};
    border-color: ${props => (props.active ? 'var(--cl-blue-700)' : '#c2cdd9')};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

export const MenuContainer = styled.ul`
  display: flex;
  flex-direction: column;
  z-index: calc(var(--cl-zindex-popover) + 1); /* Below button but above canvas */
  background-color: var(--cl-background-default);
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.24);
  border-radius: var(--cl-radius-3);
  overflow: visible; /* Changed from hidden to allow submenus to show */
  
  list-style: none;
  margin: 0;
  min-width: 150px;
  max-width: 240px;
  padding: 0;
  position: absolute;
  top: calc(100% + 8px); /* 8px gap below the button */
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  
  /* Prevent react-flow from hijacking scroll events */
  &.nowheel {
    pointer-events: auto;
  }
  
  /* Initial state for animation */
  @keyframes slideDownAndFadeIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
  
  animation: slideDownAndFadeIn var(--cl-duration-200) var(--cl-motion-ease-out);
`;

export const MenuOptionContainer = styled.li`
  display: flex;
  align-items: center;
  gap: var(--cl-space-2);
  padding: var(--cl-space-3);
  font-size: var(--cl-font-size-2);
  font-family: var(--cl-font-family-default);
  cursor: pointer;
  position: relative;
  justify-content: space-between;
  
  &:focus {
    outline: none;
    background-color: var(--cl-gray-200);
  }

  &:hover {
    background-color: var(--cl-gray-200);
  }
  
  /* First item border radius - only apply when no submenu is open */
  &:first-child:not(:hover) {
    border-top-left-radius: var(--cl-radius-3);
    border-top-right-radius: var(--cl-radius-3);
  }
  
  /* Last item border radius - only apply when no submenu is open */
  &:last-child:not(:hover) {
    border-bottom-left-radius: var(--cl-radius-3);
    border-bottom-right-radius: var(--cl-radius-3);
  }
`;

export const MenuOptionText = styled.span`
  font-size: var(--cl-font-size-2);
  line-height: 1.5;
  color: var(--cl-text-default);
  white-space: nowrap;
`;

export const CaretIcon = styled.span`
  margin-left: var(--cl-space-2);
  color: var(--cl-gray-500);
  font-size: 16px;
  display: flex;
  align-items: center;
  
  &::after {
    content: '›';
  }
`;

export const SubMenuContainer = styled.div<{ width?: string }>`
  display: flex;
  flex-direction: column;
  z-index: calc(var(--cl-zindex-popover) + 1); /* Same level as main menu */
  background-color: var(--cl-background-default);
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.24);
  border-radius: var(--cl-radius-3);
  overflow: hidden;
  width: ${props => props.width || '240px'};
  position: absolute;
  top: 0;
  left: 100%;
  margin-left: 4px;
  animation-duration: var(--cl-duration-250);
  animation-timing-function: var(--cl-motion-ease-out);
  animation-name: ${slideDownAndFade};
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: var(--cl-space-3);
  border: none;
  border-bottom: 1px solid var(--cl-border-secondary);
  font-size: var(--cl-font-size-2);
  font-family: var(--cl-font-family-default);
  box-sizing: border-box;
  background-color: var(--cl-background-default);
  color: var(--cl-text-default);
  
  &:focus {
    outline: none;
    border-bottom-color: var(--cl-blue-500);
  }
  
  &::placeholder {
    color: var(--cl-text-secondary);
  }
`;

export const SubMenuList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--cl-gray-300);
    border-radius: 4px;
    
    &:hover {
      background: var(--cl-gray-400);
    }
  }
`;

export const SubMenuOption = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cl-space-2);
  padding: var(--cl-space-3);
  font-size: var(--cl-font-size-2);
  font-family: var(--cl-font-family-default);
  cursor: pointer;
  position: relative;
  color: var(--cl-text-default);
  
  &:focus {
    outline: none;
    background-color: var(--cl-gray-200);
  }

  &:hover {
    background-color: var(--cl-gray-200);
  }
`;

export const ActionButton = styled.button.attrs(() => ({
  type: 'button',
}))`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--cl-space-2);
  background-color: var(--cl-background-default);
  border: none;
  border-top: 1px solid var(--cl-border-secondary);
  padding: var(--cl-space-3);
  width: 100%;
  font-size: var(--cl-font-size-2);
  font-family: var(--cl-font-family-default);
  color: var(--cl-blue-500);
  cursor: pointer;
  transition: background-color var(--cl-duration-200) var(--cl-motion-ease-out);
  
  &:hover {
    background-color: var(--cl-gray-200);
  }
  
  &:focus {
    outline: none;
    background-color: var(--cl-gray-200);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const NoResults = styled.div`
  padding: var(--cl-space-4);
  text-align: center;
  color: var(--cl-text-secondary);
  font-size: var(--cl-font-size-2);
  font-family: var(--cl-font-family-default);
  font-style: italic;
`;
